import { useEffect, useRef, useState } from 'react';
import { line, fill, POINT,
  CHECKLIST_ANSWER_KIND_LABEL_KEYS, Color, Size, articleRoute, workInstructionRoute, checklistAnswerProblem, checklistAnswerText, checklistProgress, formatDateTime, procedureDetailText, procedureText, signOffProblem, t,
  type ArticleView, type ChecklistAnswerInput, type ChecklistProcedure, type JobChecklistAnswer, type JobChecklistItem,
} from '@bms2/shared';
import { Button, NumericInput, Select, TextInput, Textarea } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { post, upload, useApi, FILES_PATH } from './api';
import { SignaturePad } from './SignaturePad';
import { openSheet } from './ViewPdfLink';
import { ArticleBody, articlePictureHref, fileStreamHref } from '../knowledge/ArticleBody';
import { articlePath } from '../knowledge/api';
import styles from './JobChecklist.module.css';


/** 🔒 The job's checklist route (piece 29; T389): its read, and each item's answer, photo and clear beneath it. */
export const jobChecklistPath = (workOrderId: string, itemId: string | null = null, act: 'answer' | 'photo' | 'clear' | 'sign-off' | null = null): string =>
  `/work-orders/${workOrderId}/checklist${itemId ? `/${itemId}${act ? `/${act}` : ''}` : ''}`;

/** What one open item's draft holds while it is being answered. */
interface Draft {
  value: number | null;
  /** 🔒 Decision 2112: a Choice's pick before it is saved. */
  choice: string;
  unitId: string;
  failing: boolean;
  note: string;
}

/**
 * 🔒 The job's checklist (piece 29; T389, P29-11; decision 2110): every item on
 * the checklists tagged to the Work Order, each answered the way it asks — a
 * tick, a reading with its unit, Pass or Fail with a note on a Fail, a photo
 * from the phone's camera or a desk upload, or a signature with the typed name
 * on a full-width pad. 🔨 An answer closes the item and a Clear reopens it; a
 * typed item is never closed by a bare tick.
 */
export function JobChecklist({ workOrderId, apiBaseUrl = '', onRead, onFailed, visitId, onPhoto }: {
  workOrderId: string;
  apiBaseUrl?: string;
  onRead?: (answer: JobChecklistAnswer) => void;
  /** Heard when the read has been refused or has failed, so the page around it stops waiting for an answer that is not coming (T3054). */
  onFailed?: () => void;
  /** 🔒 Decision 4320: the visit whose Service Report the checklist stands on, which a photo answer is posted on as well. */
  visitId?: string;
  /** Heard once a photo answer is through, so the report around it reads the entry it posted. */
  onPhoto?: () => void;
}) {
  const read = useApi<JobChecklistAnswer>(jobChecklistPath(workOrderId), apiBaseUrl);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const [answer, setAnswer] = useState<JobChecklistAnswer | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [signing, setSigning] = useState<{ itemId: string; name: string; image: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ itemId: string | null; message: string } | null>(null);
  const photoInput = useRef<HTMLInputElement | null>(null);
  const [photoFor, setPhotoFor] = useState<string | null>(null);
  const [signingOff, setSigningOff] = useState<{ itemId: string; reason: string } | null>(null);
  // 🔒 Decision 2280: the procedure open at full width over the checklist, which stays mounted beneath it, so Back returns where it stood.
  const [reading, setReading] = useState<ChecklistProcedure | null>(null);
  // 🔒 Decision 2111: the page around the checklist hears each read, so the visit's sheet can name what stands unanswered.
  useEffect(() => {
    if (read.state.kind === 'ready') onRead?.(read.state.data);
    else if (read.state.kind === 'failed') onFailed?.();
  }, [read.state, onRead, onFailed]);

  const settle = (outcome: { ok: true; data: JobChecklistAnswer } | { ok: false; message: string }, itemId: string | null) => {
    setBusy(false);
    if (!outcome.ok) {
      setError({ itemId, message: outcome.message });
      return false;
    }
    setError(null);
    setAnswer(outcome.data);
    onRead?.(outcome.data);
    return true;
  };
  const send = async (item: JobChecklistItem, input: ChecklistAnswerInput) => {
    const problem = checklistAnswerProblem(item.kind, input);
    if (problem) {
      setError({ itemId: item.id, message: problem });
      return false;
    }
    setBusy(true);
    return settle(await post<JobChecklistAnswer>(jobChecklistPath(workOrderId, item.id, 'answer'), input, apiBaseUrl), item.id);
  };
  const clear = async (item: JobChecklistItem) => {
    setBusy(true);
    settle(await post<JobChecklistAnswer>(jobChecklistPath(workOrderId, item.id, 'clear'), {}, apiBaseUrl), item.id);
  };
  const takePhoto = async (itemId: string, file: File | undefined) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    // 🔒 Decision 4320 (the owner's A4a): taken on a visit's report, the picture is posted under the clock's line there as well.
    if (visitId) form.append('visitId', visitId);
    setBusy(true);
    if (settle(await upload<JobChecklistAnswer>(jobChecklistPath(workOrderId, itemId, 'photo'), form, apiBaseUrl), itemId)) onPhoto?.();
  };
  const signOff = async (itemId: string, reason: string) => {
    setBusy(true);
    return settle(await post<JobChecklistAnswer>(jobChecklistPath(workOrderId, itemId, 'sign-off'), { reason }, apiBaseUrl), itemId);
  };
  const viewPhoto = async (fileId: string) => {
    const opened = await openSheet(`${apiBaseUrl}${FILES_PATH}/${fileId}/stream`);
    if (opened.outcome !== 'opened') setError({ itemId: null, message: opened.message });
  };

  return (
    <AsyncStatePanel state={read.state} emptyAction={t('checklist.job.none')} onRetry={read.reload}>
      {(fetched) => {
        const current = answer ?? fetched;
        // A read missing its list draws as an empty checklist rather than breaking the page around it.
        const a = { ...current, items: current.items ?? [] };
        const progress = checklistProgress(a.items);
        const draftOf = (item: JobChecklistItem): Draft => drafts[item.id] ?? { value: null, choice: '', unitId: item.unitId ?? '', failing: false, note: '' };
        const setDraft = (item: JobChecklistItem, patch: Partial<Draft>) => setDrafts((now) => ({ ...now, [item.id]: { ...draftOf(item), ...patch } }));
        let group: string | null = null;
        return (
          <div className={styles.checklist} data-job-checklist={a.workOrderId} data-job-checklist-closed={progress.closed} data-job-checklist-total={progress.total}>
            {a.items.length === 0 ? <p className={styles.hint}>{t('checklist.job.none')}</p> : <p className={styles.hint}>{fill(t('checklist.job.progress'), { closed: String(progress.closed), total: String(progress.total) })}</p>}
            {!a.canAnswer && a.items.length > 0 ? <p className={styles.hint} data-job-checklist-read-only="true">{t('checklist.job.readOnly')}</p> : null}
            {error && error.itemId === null ? <p className={styles.problem} role="alert">{error.message}</p> : null}
            <input
              ref={photoInput}
              className={styles.file}
              type="file"
              accept="image/*"
              capture="environment"
              name="checklist-photo"
              aria-hidden="true"
              tabIndex={-1}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (photoFor) void takePhoto(photoFor, file);
              }}
            />
            <ul className={styles.items}>
              {a.items.map((item) => {
                // 🔒 Decision 12580: a checklist's heading stands once, keyed on the checklist itself where the read names it.
                const groupKey = item.checklistId ?? item.checklistName;
                const heading = groupKey !== group ? item.checklistName : null;
                group = groupKey;
                const instruction = heading && item.checklistId ? (a.workInstructions ?? []).find((row) => row.checklistId === item.checklistId) ?? null : null;
                const d = draftOf(item);
                const unitLabel = item.units.find((u) => u.id === item.unitId)?.label ?? '';
                const answered = item.answer;
                return (
                  <li key={item.id} className={styles.item} data-checklist-item={item.id} data-checklist-kind={item.kind} data-checklist-status={item.status} data-checklist-out-of-range={item.outOfRange ? 'true' : 'false'}>
                    {heading ? (
                      <div className={styles.groupLine}>
                        <h3 className={styles.group}>{heading}</h3>
                        {/* 🔒 Decision 12580: Open Work Instruction beside a list that names one, opening the article in its working mode (decision 12581). */}
                        {instruction ? <a className={styles.procedureLink} href={workInstructionRoute(instruction.procedure.articleId, a.workOrderId, instruction.checklistId)} data-checklist-work-instruction={instruction.checklistId} data-checklist-work-instruction-article={instruction.procedure.articleId}>{t('checklist.job.openWorkInstruction')}</a> : null}
                      </div>
                    ) : null}
                    <div className={styles.head}>
                      <span className={styles.itemTitle}>{item.title}</span>
                      <span className={styles.kind}>{line(t(CHECKLIST_ANSWER_KIND_LABEL_KEYS[item.kind]), POINT, t(item.status === 'Open' ? 'checklist.job.open' : 'checklist.job.done'))}</span>
                    </div>
                    {item.description ? <p className={styles.hint}>{item.description}</p> : null}
                    {item.procedure ? (
                      <div className={styles.procedure} data-checklist-procedure={item.id} data-checklist-procedure-article={item.procedure.articleId} data-checklist-procedure-archived={item.procedure.archived ? 'true' : 'false'}>
                        <span className={styles.hint}>{t('checklist.job.procedure')}</span>
                        <a className={styles.procedureLink} href={articleRoute(item.procedure.articleId)} target="_blank" rel="noreferrer">{procedureText(item.procedure)}</a>
                        {item.completedAgainstVersion != null ? <span className={styles.hint} data-checklist-completed-against={item.completedAgainstVersion}>{fill(t('checklist.job.completedAgainst'), { n: String(item.completedAgainstVersion) })}</span> : null}
                        <Button size={Size.Small} color={Color.Inactive} text={[t('checklist.job.openProcedure')]} actions={[() => setReading(item.procedure ?? null)]} />
                      </div>
                    ) : null}
                    {item.kind === 'Reading' && (item.lowLimit !== null || item.highLimit !== null) ? <p className={styles.hint}>{rangeText(item, unitLabel)}</p> : null}
                    {item.signedOff ? <p className={styles.flag} data-checklist-signed-off={item.id}>{fill(t('checklist.job.signedOff'), { name: item.signedOff.by ?? '', reason: item.signedOff.reason })}</p> : null}
                    {answered ? (
                      <div className={styles.answer} data-checklist-answer={item.id}>
                        <span className={item.outOfRange || answered.passed === false ? styles.flag : styles.value} data-checklist-flag={item.outOfRange ? 'out-of-range' : answered.passed === false ? 'fail' : 'none'}>{checklistAnswerText(item)}</span>
                        {item.kind === 'Photo' && answered.fileId ? <Button size={Size.Small} color={Color.Inactive} text={[t('checklist.job.viewPhoto')]} actions={[() => void viewPhoto(answered.fileId!)]} /> : null}
                        {answered.answeredBy && answered.answeredAt ? <span className={styles.hint}>{fill(t('checklist.job.answeredBy'), { name: answered.answeredBy, when: formatDateTime(answered.answeredAt, zone, dateFormat) })}</span> : null}
                        {a.canAnswer ? <span data-checklist-clear={item.id}><Button size={Size.Small} color={Color.Inactive} text={[t('checklist.job.clear')]} disabled={busy} actions={[() => void clear(item)]} /></span> : null}
                      </div>
                    ) : a.canAnswer && item.status === 'Open' ? (
                      <div className={styles.controls} data-checklist-controls={item.id}>
                        {item.kind === 'Tick' ? <Button size={Size.Small} color={Color.Navy} text={[t('checklist.job.markDone')]} disabled={busy} actions={[() => void send(item, { kind: 'Tick' })]} /> : null}
                        {item.kind === 'Reading' ? (
                          <>
                            <NumericInput size={Size.Small} kind="number" label={t('checklist.job.reading')} value={d.value} onChange={(value) => setDraft(item, { value })} name={`checklist-reading-${item.id}`} />
                            {item.units.length > 1 ? (
                              <Select size={Size.Small} label={t('checklist.job.unit')} value={d.unitId} onChange={(unitId) => setDraft(item, { unitId })} options={item.units.map((u) => ({ value: u.id, label: u.label }))} />
                            ) : <span className={styles.hint}>{unitLabel}</span>}
                            <Button size={Size.Small} color={Color.Navy} text={[t('checklist.job.save')]} disabled={busy || d.value === null} actions={[() => void send(item, { kind: 'Reading', value: d.value === null ? '' : String(d.value), unitId: d.unitId || null })]} />
                          </>
                        ) : null}
                        {item.kind === 'PassFail' ? (
                          d.failing ? (
                            <>
                              <Textarea size={Size.Large} label={t('checklist.job.failNote')} value={d.note} onChange={(note) => setDraft(item, { note })} name={`checklist-fail-${item.id}`} required />
                              <Button size={Size.Small} color={Color.Warning} text={[t('checklist.job.saveFail')]} disabled={busy || d.note.trim() === ''} actions={[() => void send(item, { kind: 'PassFail', passed: false, note: d.note })]} />
                              <Button size={Size.Small} color={Color.Inactive} text={[t('modal.cancel')]} actions={[() => setDraft(item, { failing: false })]} />
                            </>
                          ) : (
                            <>
                              <Button size={Size.Small} color={Color.Navy} text={[t('checklist.job.pass')]} disabled={busy} actions={[() => void send(item, { kind: 'PassFail', passed: true })]} />
                              <Button size={Size.Small} color={Color.Warning} text={[t('checklist.job.fail')]} disabled={busy} actions={[() => setDraft(item, { failing: true })]} />
                            </>
                          )
                        ) : null}
                        {item.kind === 'Choice' ? (
                          <>
                            <Select size={Size.Medium} label={t('checklist.job.choice')} value={d.choice} onChange={(choice) => setDraft(item, { choice })} options={item.options.map((option) => ({ value: option, label: option }))} placeholder={t('checklist.job.choice')} dataAttribute={{ 'data-checklist-choice': item.id }} />
                            <Button size={Size.Small} color={Color.Navy} text={[t('checklist.job.save')]} disabled={busy || d.choice === ''} actions={[() => void send(item, { kind: 'Choice', choice: d.choice })]} />
                          </>
                        ) : null}
                        {item.kind === 'Photo' ? (
                          <Button size={Size.Small} color={Color.Navy} text={[t('checklist.job.takePhoto')]} disabled={busy} actions={[() => { setPhotoFor(item.id); photoInput.current?.click(); }]} />
                        ) : null}
                        {item.kind === 'Signature' ? (
                          <Button size={Size.Small} color={Color.Navy} text={[t('checklist.job.sign')]} disabled={busy} actions={[() => { setError(null); setSigning({ itemId: item.id, name: '', image: null }); }]} />
                        ) : null}
                        {a.canSignOff ? <span data-checklist-sign-off={item.id}><Button size={Size.Small} color={Color.Inactive} text={[t('checklist.job.signOff')]} disabled={busy} actions={[() => { setError(null); setSigningOff({ itemId: item.id, reason: '' }); }]} /></span> : null}
                      </div>
                    ) : null}
                    {error && error.itemId === item.id && !signing && !signingOff ? <p className={styles.problem} role="alert" data-checklist-error={item.id}>{error.message}</p> : null}
                  </li>
                );
              })}
            </ul>
            {signing ? (() => {
              const item = a.items.find((row) => row.id === signing.itemId);
              if (!item) return null;
              const input: ChecklistAnswerInput = { kind: 'Signature', signerName: signing.name, image: signing.image ?? '' };
              const problem = checklistAnswerProblem('Signature', input) ?? (signing.image ? null : t('checklist.job.pad'));
              return (
                <Modal
                  key={signing.itemId}
                  open
                  width="wide"
                  title={t('checklist.job.signTitle')}
                  subtitle={item.title}
                  onCancel={() => setSigning(null)}
                  dataModal="checklist-sign"
                  actions={[
                    { kind: 'primary', label: t('checklist.job.save'), disabled: busy || problem !== null, ...(problem ? { disabledReason: problem } : {}), onAct: () => void send(item, input).then((ok) => { if (ok) setSigning(null); }) },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: () => setSigning(null) },
                  ]}
                >
                  {error && error.itemId === item.id ? <p className={styles.problem} role="alert">{error.message}</p> : null}
                  <TextInput size={Size.Large} label={t('checklist.job.signerName')} value={signing.name} onChange={(name) => setSigning({ ...signing, name })} name="checklist-signer-name" required />
                  <div className={styles.pad}>
                    <SignaturePad key={signing.itemId} name="checklist-signature" label={t('checklist.job.pad')} onChange={(image) => setSigning((now) => (now ? { ...now, image } : now))} />
                  </div>
                </Modal>
              );
            })() : null}
            {reading ? <ProcedureReader key={reading.articleId} procedure={reading} apiBaseUrl={apiBaseUrl} onBack={() => setReading(null)} /> : null}
            {signingOff ? (() => {
              const item = a.items.find((row) => row.id === signingOff.itemId);
              if (!item) return null;
              const problem = signOffProblem(signingOff.reason);
              return (
                <Modal
                  key={`off-${signingOff.itemId}`}
                  open
                  title={t('checklist.job.signOffTitle')}
                  subtitle={item.title}
                  onCancel={() => setSigningOff(null)}
                  dataModal="checklist-sign-off"
                  actions={[
                    { kind: 'primary', label: t('checklist.job.signOff'), disabled: busy || problem !== null, ...(problem ? { disabledReason: problem } : {}), onAct: () => void signOff(item.id, signingOff.reason).then((ok) => { if (ok) setSigningOff(null); }) },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: () => setSigningOff(null) },
                  ]}
                >
                  <p className={styles.hint}>{t('checklist.job.signOffHint')}</p>
                  {error && error.itemId === item.id ? <p className={styles.problem} role="alert">{error.message}</p> : null}
                  <Textarea size={Size.Large} label={t('checklist.job.signOffReason')} value={signingOff.reason} onChange={(reason) => setSigningOff({ ...signingOff, reason })} name="checklist-sign-off-reason" required />
                </Modal>
              );
            })() : null}
          </div>
        );
      }}
    </AsyncStatePanel>
  );
}

/**
 * 🔒 Open Procedure (decision 2280): the cited article's newest published
 * words in a window as wide as the screen allows, read through the article's
 * own route, so the read guard that named it decides again. Back closes the
 * window and the checklist beneath it is where it stood.
 */
function ProcedureReader({ procedure, apiBaseUrl, onBack }: { procedure: ChecklistProcedure; apiBaseUrl: string; onBack: () => void }) {
  const read = useApi<ArticleView>(articlePath(procedure.articleId), apiBaseUrl);
  return (
    <Modal open width="document" height="tall" title={procedure.title} {...(procedureDetailText(procedure) ? { subtitle: procedureDetailText(procedure) } : {})} onCancel={onBack} dataModal="checklist-procedure" actions={[{ kind: 'close', label: t('checklist.job.back'), onAct: onBack }]}>
      <AsyncStatePanel state={read.state} emptyAction={t('checklist.job.procedureMissing')} onRetry={read.reload}>
        {(view) => (view.shown ? <ArticleBody html={view.shown.bodyRichText} fileHref={fileStreamHref(apiBaseUrl)} pictureHref={articlePictureHref(apiBaseUrl, procedure.articleId)} /> : <p className={styles.hint}>{t('checklist.job.procedureMissing')}</p>)}
      </AsyncStatePanel>
    </Modal>
  );
}

/** A Reading's accepted range in its own unit, either end open. */
function rangeText(item: JobChecklistItem, unitLabel: string): string {
  const unit = unitLabel;
  if (item.lowLimit !== null && item.highLimit !== null) return fill(t('checklist.job.range'), { low: String(item.lowLimit), high: String(item.highLimit), unit });
  if (item.lowLimit !== null) return fill(t('checklist.job.rangeFrom'), { low: String(item.lowLimit), unit });
  return fill(t('checklist.job.rangeTo'), { high: String(item.highLimit), unit });
}
